/**
 * copieDeForme — ce que `copy_format` (le pinceau, #1869, spec #1858) copie
 * d'un paragraphe modèle, et ce qu'il faut écrire sur un paragraphe cible pour
 * qu'il montre la même forme.
 *
 * Module pur : l'exécuteur (`copyFormatExecutor.ts`) lui demande quoi écrire,
 * la vérification (`verificationDeCopie.ts`) relit ce qu'il a demandé. Ce que
 * chaque écriture devient pour l'éditeur reste dit par `reglagesDeParagraphe.ts`
 * et `reglagesDeTexte.ts` : ce module n'y ajoute que la DEMANDE, tirée du modèle.
 *
 * ## « La forme du modèle »
 *
 * C'est la forme que l'avocat VOIT : la valeur résolue de chaque réglage, que
 * le style nommé ou la main la donne. Le résultat se juge ainsi : pour chaque
 * réglage copié, la valeur lue sur la cible après l'écriture est celle lue sur
 * le modèle. L'origine peut différer — une valeur que le modèle tient de son
 * style peut être posée à la main sur la cible.
 *
 * ## Ce qui est copié
 *
 * | part | réglages | écriture |
 * |---|---|---|
 * | style nommé | `styleId` | `styles.paragraph.setStyleRef`, une par paragraphe |
 * | paragraphe | alignement ; retraits gauche, droit, première ligne ; espacement avant, après ; interligne | l'alignement dans l'étape de plan ; `setIndentation`, `setSpacing`, une par paragraphe |
 * | bordure | les quatre côtés : type de trait, épaisseur, écart au texte, couleur | `setBorder` / `clearBorder`, une par côté à changer |
 * | texte | gras, italique, souligné, surlignage, couleur, police, taille ; toute autre forme de caractère posée à la main sur la cible est RETIRÉE | l'étape de plan du paragraphe (`format.apply`) |
 *
 * **La forme de caractère de la cible devient celle du modèle, toute
 * entière** : ce que la cible porte à la main hors des sept réglages (barré,
 * capitales, exposant, ombrage de caractère…) est retiré par la même étape,
 * réglage nul par réglage nul (`remiseAZero.ts`, `REGLAGE_QUI_EFFACE`). Ce que
 * le passage RETENU du modèle porte à la main hors des sept n'est pas lu en valeur par le lecteur
 * de forme : il n'est pas copié, il est nommé
 * ({@link FormeCopiee.texteNonCopie}) — et retiré des cibles qui en portent.
 * Ce que l'éditeur ne sait pas retirer est nommé aussi. Le masquage d'un
 * texte, son sens d'écriture, sa grille ne sont pas de la forme : ni retirés,
 * ni nommés (`remiseAZero.ts`, `HORS_FORME_DE_CARACTERE`). L'appel de note de
 * bas de page d'une cible n'est pas touché par l'étape de bloc : il garde son
 * exposant, et ne reçoit pas la forme de texte du modèle (mesure dans l'en-tête
 * de `remiseAZero.ts`).
 *
 * **Du paragraphe, la structure reste** : saut de page avant, niveau de plan,
 * numérotation, tabulations, forme de la marque de paragraphe, côtés `between`
 * et `bar` d'une bordure. L'ombrage de paragraphe et les enchaînements restent
 * aussi ; quand une cible en porte à la main et que le modèle non, le reçu le
 * nomme ({@link EcrituresDeCopie.paragrapheGarde}).
 *
 * ## Copie exacte
 *
 * Retraits, espacement et interligne que le modèle pose à la main sont recopiés
 * dans les unités du fichier ({@link FormeCopiee.brut}), pas reconvertis depuis
 * les centimètres et les points que rend le lecteur. Un interligne fixé en
 * points (`exact`, `atLeast`) posé à la main sur le modèle est recopié tel
 * quel ; porté par son style, il ne se lit pas et n'est pas copié. Une valeur
 * que le modèle tient de son style et qu'il faut écrire à la main sur une cible
 * (liste, valeur posée à la main sur la cible) passe, elle, par la valeur lue.
 * La décision d'écrire et le jugement de la relecture se font aussi sur la
 * valeur du fichier ({@link ecartsAuBrut}) : une cible à 283 twips sous un
 * modèle à 284 est réécrite, et dite différente si elle ne l'a pas été.
 *
 * ## Une forme de texte, pas plusieurs
 *
 * La copie pose UNE forme de texte sur tout le texte de la cible : celle du
 * passage le plus long du modèle (le premier, à longueur égale, espaces non
 * comptés). Un mot en gras au milieu du modèle n'est donc pas reproduit. Le
 * reçu dit quel passage a été retenu (`format.copy.textFormFrom`).
 *
 * ## Ce qui ne se lit pas n'est pas copié
 *
 * Un réglage que le lecteur de forme rend « non lisible » sur le modèle
 * (couleur de thème, interligne fixé en points, taille d'un exposant, trait de
 * bordure sans épaisseur, style absent du fichier) n'est copié sur aucune
 * cible : il est nommé ({@link FormeCopiee.nonCopies}), jamais deviné. Pour la
 * bordure, un seul côté non lisible — ou d'un type de trait dont l'écriture
 * n'est pas mesurée (`reglagesDeParagraphe.ts`, `TYPES_DE_TRAIT_MESURES`) —
 * fait laisser les quatre.
 *
 * ## Ce qui est mesuré, et dont ces règles dépendent
 *
 * Sonde `tests/ws-mocked/mesure-copy-format.spec.ts` (SuperDoc 2.15.1,
 * 2026-10-04, éditeur en mode suggestion, appels en direct) :
 *
 * - **`setStyleRef` ne change que `w:pStyle`** (S2, S5) : la forme directe du
 *   paragraphe, celle de son texte, sa numérotation et sa structure restent.
 *   `setStyle`, lui, efface la forme directe du TEXTE (S1) — celle que le plan
 *   de la copie vient d'écrire : il n'est pas employé. Une écriture, une entrée
 *   d'historique, aucune étape de plan (S9).
 * - **Le même style reposé par `setStyleRef` est accepté et laisse une entrée
 *   d'historique** (S3b) : une cible qui porte déjà le style n'est pas écrite.
 * - **Un style que le fichier ne définit pas est refusé** (S4b, `INVALID_INPUT`
 *   rendu) : il n'est pas copié.
 * - **La numérotation directe survit à la pose d'un style** (S6, S6b), et **un
 *   style qui porte une numérotation numérote le paragraphe qui le reçoit**
 *   (S7).
 * - **Une révision en attente portée par le paragraphe n'est pas effacée**
 *   (M1 à M4, A3, A4, D1, D2) : l'élément `w:pPrChange` et la marque de
 *   paragraphe insérée, supprimée ou à forme en attente restent au fichier
 *   après l'étape de plan, `setStyleRef`, `setIndentation`, `setSpacing` et
 *   `setBorder`. Ce que devient alors ce que le `w:pPrChange` PROPOSE est dit
 *   une fois, dans l'en-tête de `ecritureDeForme.ts` ; la garde
 *   `canvas-copy-format.spec.ts` le relit attribut par attribut. Une marque
 *   DÉPLACÉE n'est pas mesurée. Une révision portée par le TEXTE fait refuser
 *   l'étape qui écrit une forme de texte (`remiseAZero.ts`) ; l'étape
 *   d'alignement seule est acceptée (A1, A2).
 * - **Une étape de plan porte une forme de texte ET un alignement** (P1) ; deux
 *   étapes sur le même paragraphe sont refusées (P4, `TARGET_CONFLICT`). La
 *   couleur `auto` et un surlignage hors des trois de `format_text` s'écrivent
 *   (P3, P3b).
 *
 * ## Ce qui en découle
 *
 * - **Les listes restent des listes.** La numérotation n'est pas de la forme :
 *   la cible garde la sienne. Les retraits d'un paragraphe numéroté tiennent à
 *   sa liste : dès que le modèle ou la cible est numéroté, les retraits ne sont
 *   pas copiés sur cette cible (comptée, nommée). Un style qui numérote n'est
 *   posé sur aucune cible ; une cible que SON style numérote garde ce style.
 * - **Ne pas écrire ce qui est déjà en place.** Le fichier n'est relu qu'après
 *   toutes les écritures : ce que la cible montrera une fois le style posé est
 *   donc PRÉVU ({@link ecrituresDeCopie}). Un réglage que la cible pose à la
 *   main garde sa valeur ; un réglage qu'elle tient de son style prendra celle
 *   que le modèle tient du sien. Quand la prévision n'est pas sûre (liste,
 *   valeur que le modèle pose à la main, réglage non lisible), le réglage est
 *   écrit. La forme de texte suit la même règle : un réglage que le passage
 *   pose à la main garde sa valeur ; sinon, sous le style posé, il prend celle
 *   que le passage retenu du modèle tient de son style — sauf style de
 *   caractère d'un côté ou de l'autre, où rien n'est prévu.
 * - **Une révision de forme de paragraphe en attente sur la cible** : la règle
 *   est celle de tous les verbes de forme, portée par le socle
 *   (`ecritureDeForme.ts`, « Un paragraphe dont la forme attend une décision »).
 * - **Deux cibles voisines** portent après la copie la même bordure : la page
 *   les peint comme un seul cadre (`reglagesDeParagraphe.ts`, « La bordure »).
 *   Le reçu compte ces voisins.
 * - **Une révision en attente sur le texte de la cible** : aucune forme de
 *   texte n'y est écrite (cible nommée) ; le style et la forme de paragraphe
 *   sont copiés. Son texte montre alors ce que donne le style posé, sous ce
 *   qu'il portait à la main — pas la forme de texte du modèle.
 */

import {
  CANVAS_PARAGRAPH_BORDER_SIDES,
  CANVAS_TEXT_FORMAT_SETTING_KEYS,
  type CanvasFormatNotCopiedReason,
  type CanvasFormatSettingKey,
  type CanvasParagraphAlignment,
  type CanvasParagraphBorderSide,
  type CanvasParagraphFormatKey,
} from '@hector/shared-types';
import { interligneEnMultiple, twipsEnCm, vingtiemesEnPt, type FormeDeParagraphe, type FormeDirecteDeParagraphe, type Reglage, type TraitDeBordure } from '@hector/docx-forme';
import {
  ecrituresDesTraits,
  ecrituresDuParagraphe,
  ecrituresEmpechees,
  estTraitMesure,
  reglagesDeParagrapheDemandes,
  type EcritureDeBordure,
  type EcritureEmpechee,
  type EcrituresDeParagraphe,
  type ReglagesDeParagraphe,
  type TraitMesure,
} from './reglagesDeParagraphe';
import { jugerLeReglage, passageLuPour, type EcritureDeReglage } from './reglagesDeTexte';
import { HORS_FORME_DE_CARACTERE, REGLAGE_QUI_EFFACE } from './remiseAZero';
import { ecritureSurLeParagraphe, type ReglagesDeTexte } from './verificationDeForme';

/** Les réglages de paragraphe qu'une copie reprend par leur valeur ; la bordure se copie par ses traits. */
export const REGLAGES_DE_PARAGRAPHE_COPIES = [
  'alignment',
  'indentLeftCm',
  'indentRightCm',
  'indentFirstLineCm',
  'spaceBeforePt',
  'spaceAfterPt',
  'lineSpacing',
] as const satisfies readonly CanvasParagraphFormatKey[];

/** Les trois retraits : ils tiennent à la liste d'un paragraphe numéroté. */
export const RETRAITS_COPIES = ['indentLeftCm', 'indentRightCm', 'indentFirstLineCm'] as const satisfies readonly CanvasParagraphFormatKey[];

/** Longueur maximale du passage retenu que le reçu cite. */
const PASSAGE_RETENU_MAX = 60;

/** Les éléments de `w:rPr` des sept réglages de texte : la copie y pose une valeur. */
const ELEMENTS_DES_SEPT: ReadonlySet<string> = new Set(['b', 'i', 'u', 'highlight', 'color', 'rFonts', 'sz']);
/**
 * Leurs jumeaux d'écriture complexe (arabe, hébreu), que Word écrit avec eux : ils ne changent rien à un texte latin.
 * Ni retirés d'une cible, ni nommés « non copiés » : l'éditeur les règle avec le réglage qu'ils doublent.
 */
const JUMEAUX_DES_SEPT: ReadonlySet<string> = new Set(['bCs', 'iCs', 'szCs']);

/** La forme de paragraphe qu'une copie ne reprend pas et qui reste sur la cible, par l'élément du fichier qui la porte. */
const FORME_DE_PARAGRAPHE_GARDEE = { shd: 'shading', keepNext: 'keepWithNext', keepLines: 'keepLines', widowControl: 'widowControl' } as const;
/** Ce qu'une cible garde de sa forme de paragraphe et que le modèle ne porte pas. */
export type FormeDeParagrapheGardee = (typeof FORME_DE_PARAGRAPHE_GARDEE)[keyof typeof FORME_DE_PARAGRAPHE_GARDEE];

/** Un interligne fixé en points, tel que le fichier l'écrit : vingtièmes de point, et sa règle. */
export interface InterligneEnPoints { line: number; lineRule: 'exact' | 'atLeast' }

/** Ce qu'une copie reprend du paragraphe modèle : ce qui s'y lit. */
export interface FormeCopiee {
  /** Le style nommé à poser. Absent : il n'est posé sur aucune cible. */
  styleId?: string;
  /** Les réglages de paragraphe lisibles sur le modèle, hors bordure, tels que le lecteur les rend. */
  paragraphe: ReglagesDeParagraphe;
  /** Retraits et espacement que le modèle pose à la main, dans les unités du fichier : la copie les recopie tels quels. */
  brut: Pick<FormeDirecteDeParagraphe, 'retraits' | 'espacement'>;
  /** L'interligne fixé en points que le modèle pose à la main : recopié tel quel, `lineSpacing` ne le portant pas. */
  interligneEnPoints?: InterligneEnPoints;
  /** Le trait de chacun des quatre côtés ; `null` : pas de trait. Absent : la bordure n'est pas copiée. */
  traits?: Record<CanvasParagraphBorderSide, TraitMesure | null>;
  /** Les réglages de texte lisibles sur le passage retenu. */
  texte: ReglagesDeTexte;
  /** Le passage du modèle dont la forme de texte est copiée, et le nombre de passages du modèle. Absent : le modèle n'a pas de texte. */
  passageRetenu?: { texte: string; passages: number };
  /**
   * Les éléments de forme de caractère que le modèle porte à la main hors des
   * sept réglages (noms du fichier : `strike`, `caps`…) : non copiés.
   */
  texteNonCopie: string[];
  /** Les réglages du modèle qui ne sont copiés sur aucune cible, avec la raison ; `border` vaut pour les quatre côtés. */
  nonCopies: Array<{ setting: CanvasFormatSettingKey; reason: CanvasFormatNotCopiedReason }>;
}

/** Vrai si la copie reprend une forme de texte : elle règle alors TOUTE la forme de caractère des cibles. */
export const copieUneFormeDeTexte = (forme: FormeCopiee): boolean => Object.keys(forme.texte).length > 0;

/** Vrai si la copie reprend au moins un réglage du modèle. */
export function aQuelqueChoseACopier(forme: FormeCopiee): boolean {
  return forme.styleId !== undefined || forme.traits !== undefined || forme.interligneEnPoints !== undefined
    || Object.keys(forme.paragraphe).length > 0 || copieUneFormeDeTexte(forme);
}

/** Le passage le plus long du modèle, espaces non comptés ; le premier à longueur égale. */
function passageDominant(modele: FormeDeParagraphe): FormeDeParagraphe['passages'][number] | undefined {
  const longueur = (texte: string): number => texte.replace(/\s/g, '').length;
  return modele.passages.reduce<FormeDeParagraphe['passages'][number] | undefined>(
    (retenu, passage) => (retenu === undefined || longueur(passage.texte) > longueur(retenu.texte) ? passage : retenu),
    undefined,
  );
}

/**
 * Ce qu'une copie reprend du paragraphe modèle (voir l'en-tête) : son style
 * nommé, ses réglages de paragraphe, les traits de sa bordure et la forme de
 * son passage le plus long — pour ce qui s'y lit ; le reste est nommé.
 */
export function formeACopier(modele: FormeDeParagraphe): FormeCopiee {
  const nonCopies: FormeCopiee['nonCopies'] = [];

  let styleId: string | undefined;
  if (modele.styleId === null || !modele.styleDefini) nonCopies.push({ setting: 'styleId', reason: 'source_has_no_named_style' });
  else if (modele.numerotation === 'du_style') nonCopies.push({ setting: 'styleId', reason: 'style_numbers_paragraphs' });
  else styleId = modele.styleId;

  // Un interligne en points posé à la main ne se lit pas en multiple, mais le fichier le porte : il se recopie.
  const { line, lineRule } = modele.aLaMain.espacement;
  const interligneEnPoints: InterligneEnPoints | undefined = line !== undefined && (lineRule === 'exact' || lineRule === 'atLeast') ? { line, lineRule } : undefined;

  const lusDuParagraphe: Partial<Record<CanvasParagraphFormatKey, string | number>> = {};
  for (const reglage of REGLAGES_DE_PARAGRAPHE_COPIES) {
    const lu = modele.paragraphe[reglage];
    if (lu.lisible) lusDuParagraphe[reglage] = lu.valeur;
    else if (!(reglage === 'lineSpacing' && interligneEnPoints !== undefined)) nonCopies.push({ setting: reglage, reason: 'unreadable_on_source' });
  }

  const traits: Partial<Record<CanvasParagraphBorderSide, TraitDeBordure | null>> = {};
  for (const cote of CANVAS_PARAGRAPH_BORDER_SIDES) {
    const lu = modele.traitsDeBordure[cote];
    if (lu.lisible) traits[cote] = lu.valeur;
  }
  const { top, left, bottom, right } = traits;
  const mesure = (trait: TraitDeBordure | null | undefined): trait is TraitMesure | null => trait === null || (trait !== undefined && estTraitMesure(trait));
  let traitsCopies: FormeCopiee['traits'];
  if (top === undefined || left === undefined || bottom === undefined || right === undefined) nonCopies.push({ setting: 'border', reason: 'unreadable_on_source' });
  else if (mesure(top) && mesure(left) && mesure(bottom) && mesure(right)) traitsCopies = { top, left, bottom, right };
  else nonCopies.push({ setting: 'border', reason: 'border_line_type_not_measured' });

  const texte: ReglagesDeTexte = {};
  const retenu = passageDominant(modele);
  for (const reglage of CANVAS_TEXT_FORMAT_SETTING_KEYS) {
    const lu = retenu?.[reglage];
    if (lu === undefined) nonCopies.push({ setting: reglage, reason: 'source_has_no_text' });
    else if (lu.lisible) texte[reglage] = lu.valeur;
    else nonCopies.push({ setting: reglage, reason: 'unreadable_on_source' });
  }

  return {
    ...(styleId === undefined ? {} : { styleId }),
    paragraphe: reglagesDeParagrapheDemandes(lusDuParagraphe),
    brut: { retraits: modele.aLaMain.retraits, espacement: modele.aLaMain.espacement },
    ...(interligneEnPoints === undefined ? {} : { interligneEnPoints }),
    ...(traitsCopies === undefined ? {} : { traits: traitsCopies }),
    texte,
    ...(retenu === undefined ? {} : { passageRetenu: { texte: retenu.texte.slice(0, PASSAGE_RETENU_MAX), passages: modele.passages.length } }),
    // Sur le passage RETENU seul : un « 1er » en exposant ailleurs dans le modèle n'est pas de la forme copiée.
    texteNonCopie: (retenu?.elementsALaMain ?? []).filter((element) => !ELEMENTS_DES_SEPT.has(element) && !JUMEAUX_DES_SEPT.has(element) && !HORS_FORME_DE_CARACTERE.has(element)),
    nonCopies,
  };
}

/** Ce que la copie écrit sur UNE cible, et ce qu'elle y laisse. Une écriture absente n'y changerait rien. */
export interface EcrituresDeCopie {
  /** Le style nommé à poser (`setStyleRef`). */
  style?: string;
  /** La forme de caractère de l'étape de plan du paragraphe (`format.apply`, `inline`) : valeurs à poser, `null` pour ce qui est retiré. */
  inline?: EcritureDeReglage['inline'];
  /** L'alignement de la même étape de plan. */
  alignement?: CanvasParagraphAlignment;
  retraits?: EcrituresDeParagraphe['retraits'];
  espacement?: EcrituresDeParagraphe['espacement'];
  /** Les écritures de bordure, une par côté à changer. Jamais vide. */
  bordure?: EcritureDeBordure[];
  /** Les écritures écartées : elles effaceraient des attributs posés à la main (`reglagesDeParagraphe.ts`). */
  empechees: EcritureEmpechee[];
  /** Vrai si les retraits ne sont pas copiés sur cette cible : elle, ou le modèle, est numérotée. */
  retraitsGardes: boolean;
  /** Vrai si le style nommé n'est pas posé sur cette cible : son style porte sa numérotation. */
  styleGarde: boolean;
  /** Vrai si la forme de texte n'est pas écrite sur cette cible : son texte porte une révision en attente. */
  texteLaisse: boolean;
  /** Les éléments de forme de caractère que la cible porte à la main et que l'éditeur ne sait pas retirer (`rPr/…` omis). */
  texteNonRetirable: string[];
  /** La forme de paragraphe que la cible porte à la main, que le modèle ne porte pas et qu'une copie ne retire pas. */
  paragrapheGarde: FormeDeParagrapheGardee[];
}

/** Un réglage qu'une cible porte à la main dans une autre valeur du fichier que le modèle, même si les deux se lisent pareil. */
export interface EcartAuBrut {
  setting: (typeof REGLAGES_DE_PARAGRAPHE_COPIES)[number];
  famille: 'retraits' | 'espacement';
  attribut: 'left' | 'right' | 'firstLine' | 'hanging' | 'before' | 'after' | 'line';
  duModele: number;
  deLaCible: number;
}

/**
 * Les réglages que le modèle ET la cible posent à la main, dans les unités du
 * fichier, avec des valeurs différentes. Une copie exacte se décide et se juge
 * là-dessus : 283 et 284 twips se lisent tous deux 0,5 cm, à la tolérance du
 * lecteur près, et ne sont pas la même valeur.
 *
 * @param brut Ce que le modèle pose à la main ({@link FormeCopiee.brut}).
 * @param aLaMain Ce que la cible pose à la main.
 */
export function ecartsAuBrut(brut: FormeCopiee['brut'], aLaMain: Pick<FormeDirecteDeParagraphe, 'retraits' | 'espacement'>): EcartAuBrut[] {
  const paires = [
    ['indentLeftCm', 'retraits', 'left', brut.retraits.left, aLaMain.retraits.left],
    ['indentRightCm', 'retraits', 'right', brut.retraits.right, aLaMain.retraits.right],
    ['indentFirstLineCm', 'retraits', 'firstLine', brut.retraits.firstLine, aLaMain.retraits.firstLine],
    ['indentFirstLineCm', 'retraits', 'hanging', brut.retraits.hanging, aLaMain.retraits.hanging],
    ['spaceBeforePt', 'espacement', 'before', brut.espacement.before, aLaMain.espacement.before],
    ['spaceAfterPt', 'espacement', 'after', brut.espacement.after, aLaMain.espacement.after],
    // L'interligne ne se compare qu'en multiple de ligne des deux côtés : fixé en points, il a sa propre copie.
    ...((brut.espacement.lineRule ?? 'auto') === 'auto' && (aLaMain.espacement.lineRule ?? 'auto') === 'auto'
      ? [['lineSpacing', 'espacement', 'line', brut.espacement.line, aLaMain.espacement.line] as const]
      : []),
  ] as const;
  return paires.flatMap(([setting, famille, attribut, duModele, deLaCible]) => (
    duModele === undefined || deLaCible === undefined || duModele === deLaCible ? [] : [{ setting, famille, attribut, duModele, deLaCible }]
  ));
}

/** Une valeur que la prévision ne sait pas dire : le réglage sera écrit. */
const NON_PREVU: Reglage<never> = { lisible: false, raison: 'non_defini' };

/** Remplace, dans une écriture, chaque valeur qui se lit comme celle que le modèle pose à la main par cette valeur du fichier. */
function auBrut<Cle extends string>(ecrit: Partial<Record<Cle, number>>, brut: Partial<Record<Cle, number>>, lire: (valeur: number) => number, cles: readonly Cle[]): void {
  for (const cle of cles) {
    const [valeur, duModele] = [ecrit[cle], brut[cle]];
    if (valeur !== undefined && duModele !== undefined && lire(valeur) === lire(duModele)) ecrit[cle] = duModele;
  }
}

/**
 * Ce qu'il faut écrire sur `cible` pour qu'elle montre la forme copiée, d'après
 * les deux paragraphes lus AVANT l'écriture (voir l'en-tête, « Ce qui en
 * découle »).
 *
 * @param forme Ce que la copie reprend du modèle ({@link formeACopier}).
 * @param modele Le paragraphe modèle, tel que lu.
 */
export function ecrituresDeCopie(forme: FormeCopiee, modele: FormeDeParagraphe, cible: FormeDeParagraphe & { nodeId: string }): EcrituresDeCopie {
  const aPoser = forme.styleId !== undefined && cible.styleId !== forme.styleId;
  // Remplacer un style qui numérote ferait perdre sa numérotation à la cible.
  const styleGarde = aPoser && cible.numerotation === 'du_style';
  const stylePose = aPoser && !styleGarde;
  const sansListe = modele.numerotation === 'aucune' && cible.numerotation === 'aucune';

  // Ce que la cible montrera pour chaque réglage une fois le style posé, avant toute autre écriture.
  const prevu = { ...cible.paragraphe };
  if (stylePose) {
    for (const reglage of REGLAGES_DE_PARAGRAPHE_COPIES) {
      const lu = cible.paragraphe[reglage];
      // Posé à la main, il garde sa valeur ; non lisible, rien n'est prévu : il sera écrit.
      if (!lu.lisible || lu.origine === 'direct') continue;
      const duModele = modele.paragraphe[reglage];
      // Sous le même style, sans liste d'un côté ni de l'autre, la cible prend ce que le modèle tient de son style.
      const sousLeStyle: Reglage<string | number> = sansListe && duModele.lisible && duModele.origine !== 'direct' ? duModele : NON_PREVU;
      Object.assign(prevu, { [reglage]: sousLeStyle });
    }
  }
  const ciblePrevue: FormeDeParagraphe = { ...cible, paragraphe: prevu };

  const demandes: ReglagesDeParagraphe = { ...forme.paragraphe };
  const retraitsGardes = !sansListe && RETRAITS_COPIES.some((reglage) => demandes[reglage] !== undefined);
  if (!sansListe) for (const reglage of RETRAITS_COPIES) delete demandes[reglage];

  const toutes = ecrituresDuParagraphe(ciblePrevue, demandes);
  const empechees = ecrituresEmpechees(ciblePrevue, toutes, demandes);
  const duParagraphe: EcrituresDeParagraphe = { ...toutes };
  for (const { famille } of empechees) delete duParagraphe[famille];

  // L'interligne en points du modèle : recopié tel que le fichier l'écrit, sur une cible qui ne le porte pas déjà à la main.
  const { interligneEnPoints } = forme;
  const enPlace = cible.aLaMain.espacement;
  if (interligneEnPoints !== undefined && (enPlace.line !== interligneEnPoints.line || enPlace.lineRule !== interligneEnPoints.lineRule)) {
    const attributs = cible.aLaMain.nonRepris.espacement;
    const dejaEmpechee = empechees.find((empechee) => empechee.famille === 'espacement');
    if (dejaEmpechee !== undefined) dejaEmpechee.reglages.push('lineSpacing');
    else if (attributs.length > 0) empechees.push({ famille: 'espacement', reglages: ['lineSpacing'], attributs });
    else {
      const { before, after } = duParagraphe.espacement ?? enPlace;
      duParagraphe.espacement = { ...(before === undefined ? {} : { before }), ...(after === undefined ? {} : { after }), ...interligneEnPoints };
    }
  }
  // Copie exacte : ce que le modèle pose à la main part dans les unités du fichier, pas reconverti depuis la valeur lue.
  if (duParagraphe.retraits !== undefined) auBrut(duParagraphe.retraits, forme.brut.retraits, twipsEnCm, ['left', 'right', 'firstLine', 'hanging']);
  if (duParagraphe.espacement !== undefined) {
    auBrut(duParagraphe.espacement, forme.brut.espacement, vingtiemesEnPt, ['before', 'after']);
    if (duParagraphe.espacement.lineRule === 'auto') auBrut(duParagraphe.espacement, forme.brut.espacement, interligneEnMultiple, ['line']);
  }

  // Une cible dont la valeur à la main se lit comme celle du modèle sans être la même au fichier est réécrite — sauf si
  // l'écriture effaçait des attributs qu'elle porte à la main : la relecture dira alors l'écart.
  const ecarts = ecartsAuBrut(forme.brut, cible.aLaMain).filter((ecart) => demandes[ecart.setting] !== undefined);
  for (const famille of ['retraits', 'espacement'] as const) {
    const aCorriger = ecarts.filter((ecart) => ecart.famille === famille);
    if (aCorriger.length === 0 || duParagraphe[famille] !== undefined) continue;
    if (cible.aLaMain.nonRepris[famille].length > 0 || empechees.some((empechee) => empechee.famille === famille)) continue;
    Object.assign(duParagraphe, { [famille]: { ...cible.aLaMain[famille], ...Object.fromEntries(aCorriger.map((ecart) => [ecart.attribut, ecart.duModele])) } });
  }

  const bordure = forme.traits === undefined
    ? []
    : ecrituresDesTraits(cible, stylePose ? modele.traitsDeBordureSousLaMain : cible.traitsDeBordureSousLaMain, forme.traits);

  // ── La forme de caractère : celle du passage retenu, sur tout le texte de la cible ; le reste, posé à la main, retiré.
  // Rien à écrire sur un paragraphe sans texte, ni quand le modèle ne donne aucune forme de texte.
  const avecTexte = cible.passages.length > 0 && copieUneFormeDeTexte(forme);
  // Le masquage, le sens d'écriture, la grille ne sont pas de la forme : ni retirés, ni nommés (`remiseAZero.ts`).
  const aLaMain = avecTexte ? cible.formeDirecte.deTexte.filter((element) => !ELEMENTS_DES_SEPT.has(element) && !JUMEAUX_DES_SEPT.has(element) && !HORS_FORME_DE_CARACTERE.has(element)) : [];
  const aRetirer = aLaMain.flatMap((element) => (REGLAGE_QUI_EFFACE[element] === undefined ? [] : [REGLAGE_QUI_EFFACE[element]]));
  const duTexte = avecTexte ? ecritureSurLeParagraphe(cible, forme.texte) : undefined;
  const valeursAPoser = duTexte !== undefined && !duTexte.sansEffet && !montreDejaLaFormeDeTexte(forme, modele, cible, stylePose);
  const texteAEcrire = valeursAPoser || aRetirer.length > 0;
  // L'éditeur refuse d'écrire une forme de texte par-dessus une révision en attente portée par le texte.
  const texteLaisse = texteAEcrire && cible.formeDirecte.revisionEnAttente.deTexte;
  const inline = { ...(valeursAPoser ? duTexte.inline : {}), ...Object.fromEntries(aRetirer.map((reglage) => [reglage, null])) };

  const deParagraphe = (paragraphe: FormeDeParagraphe): readonly string[] => paragraphe.formeDirecte.deParagraphe;
  return {
    ...(stylePose && forme.styleId !== undefined ? { style: forme.styleId } : {}),
    ...(texteAEcrire && !texteLaisse ? { inline } : {}),
    ...(duParagraphe.alignement === undefined ? {} : { alignement: duParagraphe.alignement }),
    ...(duParagraphe.retraits === undefined ? {} : { retraits: duParagraphe.retraits }),
    ...(duParagraphe.espacement === undefined ? {} : { espacement: duParagraphe.espacement }),
    ...(bordure.length === 0 ? {} : { bordure }),
    empechees,
    retraitsGardes,
    styleGarde,
    texteLaisse,
    texteNonRetirable: aLaMain.filter((element) => REGLAGE_QUI_EFFACE[element] === undefined),
    paragrapheGarde: Object.entries(FORME_DE_PARAGRAPHE_GARDEE).flatMap(([element, nom]) => (deParagraphe(cible).includes(element) && !deParagraphe(modele).includes(element) ? [nom] : [])),
  };
}

/**
 * Vrai si chaque passage de la cible montrera, pour chaque réglage de texte
 * copié, la valeur du modèle sans qu'on l'écrive : il la pose déjà à la main,
 * ou — rien à la main — il la tiendra du style, comme le passage retenu du
 * modèle la tient du sien.
 *
 * @param stylePose Vrai si le style du modèle va être posé sur la cible.
 */
function montreDejaLaFormeDeTexte(forme: FormeCopiee, modele: FormeDeParagraphe, cible: FormeDeParagraphe, stylePose: boolean): boolean {
  const retenu = passageDominant(modele);
  // Un style de caractère passe devant le style du paragraphe : sous lui, rien n'est prévu.
  const sansStyleDeCaractere = !cible.formeDirecte.stylesDeCaractere && !modele.formeDirecte.stylesDeCaractere;
  return cible.passages.every((passage) => CANVAS_TEXT_FORMAT_SETTING_KEYS.every((reglage) => {
    const demande = forme.texte[reglage];
    if (demande === undefined) return true;
    const lu = passageLuPour(passage, reglage);
    // Sans style à poser, ou posé à la main : ce que le passage montre aujourd'hui, il le montrera encore.
    if (!stylePose || lu.aLaMain) return jugerLeReglage(reglage, demande, lu).issue === 'conforme';
    const duModele = retenu?.[reglage];
    return sansStyleDeCaractere && duModele?.lisible === true && duModele.origine !== 'direct' && retenu?.aLaMain.includes(reglage) === false;
  }));
}

/** Les côtés `between` et `bar` qu'un paragraphe porte à la main dans sa bordure : la copie ne les lit ni ne les écrit. */
export function cotesHorsCopie(paragraphe: FormeDeParagraphe): Array<'between' | 'bar'> {
  return (['between', 'bar'] as const).filter((cote) => paragraphe.formeDirecte.cotesDeBordure.includes(cote));
}
